import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  LoginRespuestaSchema,
  SesionSchema,
  type LoginRespuesta,
  type Sesion,
  type UsuarioSesion,
} from "@tentacion/shared";
import { apiFetch, establecerAccessToken, registrarRefrescador } from "./api";

/**
 * Estado de autenticación real (JWT de acceso en memoria + refresh token en
 * cookie httpOnly + PIN rápido para meseras + 2FA TOTP para admin —
 * sección 4 del proyecto). Reemplaza el selector de usuario temporal.
 *
 * "requiere2fa": el admin ya puso usuario/password correctos pero tiene 2FA
 * activo y falta el código.
 * "requiereConfiguracion2fa": caso del nodo nube (2FA obligatorio para admin,
 * sección 4); en el nodo local (Fase 1) este estado no ocurre, pero el
 * frontend lo soporta para no romper cuando exista la nube.
 */
type EstadoAuth =
  | { tipo: "cargando" }
  | { tipo: "anonimo" }
  | { tipo: "requiere2fa"; tokenTemporal: string }
  | { tipo: "requiereConfiguracion2fa"; tokenTemporal: string }
  | { tipo: "autenticado"; usuario: UsuarioSesion };

interface AuthContexto {
  estado: EstadoAuth;
  usuario: UsuarioSesion | null;
  /** Compatibilidad con el código existente (queries), que solo necesita saber si hay sesión. */
  usuarioId: string | null;
  login: (usuario: string, password: string) => Promise<void>;
  loginPin: (usuario: string, pin: string) => Promise<void>;
  verificar2fa: (codigo: string) => Promise<void>;
  activarConfiguracion2fa: (codigo: string) => Promise<void>;
  cancelar2fa: () => void;
  salir: () => Promise<void>;
}

const Contexto = createContext<AuthContexto | undefined>(undefined);

let promesaRefrescoEnCurso: Promise<string | null> | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoAuth>({ tipo: "cargando" });

  function aplicarSesion(sesion: Sesion): void {
    establecerAccessToken(sesion.accessToken);
    setEstado({ tipo: "autenticado", usuario: sesion.usuario });
  }

  function aplicarResultadoLogin(resultado: LoginRespuesta): void {
    if ("requiere2fa" in resultado) {
      setEstado({ tipo: "requiere2fa", tokenTemporal: resultado.tokenTemporal });
      return;
    }
    if ("requiereConfiguracion2fa" in resultado) {
      setEstado({ tipo: "requiereConfiguracion2fa", tokenTemporal: resultado.tokenTemporal });
      return;
    }
    aplicarSesion(resultado);
  }

  function refrescarSilencioso(): Promise<string | null> {
    if (promesaRefrescoEnCurso) {
      return promesaRefrescoEnCurso;
    }

    promesaRefrescoEnCurso = (async () => {
      try {
        const sesion = await apiFetch<Sesion>("/auth/refresh", { method: "POST", schema: SesionSchema });
        aplicarSesion(sesion);
        return sesion.accessToken;
      } catch (e: any) {
        // Solo marcar como anónimo si el servidor respondió explícitamente con 401
        // (indica que la sesión venció o no existe). No desloguear si es una desconexión
        // temporal de red o fallo de conexión momentáneo.
        const esError401 = e?.status === 401;
        if (esError401) {
          establecerAccessToken(null);
          setEstado({ tipo: "anonimo" });
        }
        return null;
      } finally {
        promesaRefrescoEnCurso = null;
      }
    })();

    return promesaRefrescoEnCurso;
  }

  // Al cargar la app (o recargar la tablet), intenta recuperar la sesión con
  // el refresh token de la cookie httpOnly antes de mostrar el login.
  useEffect(() => {
    void refrescarSilencioso();
  }, []);

  // apiFetch usa esto para reintentar UNA vez, en silencio, cuando el access
  // token expira a mitad de uso (dura poco a propósito, sección 4).
  useEffect(() => {
    registrarRefrescador(refrescarSilencioso);
    return () => registrarRefrescador(null);
  }, []);

  const valor = useMemo<AuthContexto>(() => {
    const usuario = estado.tipo === "autenticado" ? estado.usuario : null;

    return {
      estado,
      usuario,
      usuarioId: usuario?.id ?? null,

      login: async (usuarioNombre, password) => {
        const resultado = await apiFetch<LoginRespuesta>("/auth/login", {
          method: "POST",
          body: { usuario: usuarioNombre, password },
          schema: LoginRespuestaSchema,
        });
        aplicarResultadoLogin(resultado);
      },

      loginPin: async (usuarioNombre, pin) => {
        const sesion = await apiFetch<Sesion>("/auth/pin", {
          method: "POST",
          body: { usuario: usuarioNombre, pin },
          schema: SesionSchema,
        });
        aplicarSesion(sesion);
      },

      verificar2fa: async (codigo) => {
        if (estado.tipo !== "requiere2fa") throw new Error("No hay una verificación de 2FA pendiente.");
        const sesion = await apiFetch<Sesion>("/auth/2fa/verificar", {
          method: "POST",
          body: { tokenTemporal: estado.tokenTemporal, codigo },
          schema: SesionSchema,
        });
        aplicarSesion(sesion);
      },

      activarConfiguracion2fa: async (codigo) => {
        if (estado.tipo !== "requiereConfiguracion2fa") {
          throw new Error("No hay una configuración de 2FA pendiente.");
        }
        const sesion = await apiFetch<Sesion>("/auth/2fa/activar-con-token", {
          method: "POST",
          body: { tokenTemporal: estado.tokenTemporal, codigo },
          schema: SesionSchema,
        });
        aplicarSesion(sesion);
      },

      cancelar2fa: () => setEstado({ tipo: "anonimo" }),

      salir: async () => {
        try {
          await apiFetch("/auth/logout", { method: "POST" });
        } catch {
          // Si el backend no responde igual queremos limpiar la sesión local.
        }
        establecerAccessToken(null);
        setEstado({ tipo: "anonimo" });
      },
    };
  }, [estado]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAuth(): AuthContexto {
  const contexto = useContext(Contexto);
  if (!contexto) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return contexto;
}
